import React from 'react'
import styled from 'styled-components'

import DocumentTitle from '../DocumentTitle'
import Link from '../Link'
import Searchbox from '../Searchbox'

import ipnLogo from './Images/LOGO_IPN.png'

const GREEN = '#00552e'
const DARK_GREEN = '#003d21'
const TEXT = '#111820'
const MUTED = '#5b6570'
const BORDER = '#d8dde1'

/* =========================================================
   PAGE
   ========================================================= */

const Page = styled.div`
  width: 100%;
  min-height: calc(100vh - 60px);
  box-sizing: border-box;

  display: flex;
  flex-direction: column;

  background: #fff;
  color: ${TEXT};
  font-family: Arial, Helvetica, sans-serif;
`

const Main = styled.main`
  width: calc(100% - clamp(24px, 3vw, 48px));
  max-width: none;

  margin: 0 auto;

  padding:
    clamp(24px, 3vw, 34px)
    0
    clamp(36px, 5vw, 70px);

  box-sizing: border-box;

  flex: 1;

  display: flex;
  flex-direction: column;
`

/* =========================================================
   BREADCRUMBS
   ========================================================= */

const Breadcrumbs = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;

  margin-bottom: clamp(28px, 4vw, 45px);

  font-size: 15px;

  a {
    color: #2674b8;
    text-decoration: underline;
  }

  span {
    color: #7b8794;
  }
`

/* =========================================================
   PAGE INTRO
   ========================================================= */

const Intro = styled.section`
  width: 100%;
  max-width: 900px;

  margin: 0 auto clamp(24px, 3vw, 38px);

  text-align: center;

  h1 {
    margin: 0 0 14px;

    font-size: clamp(34px, 3vw, 50px);
    line-height: 1.1;
  }

  p {
    max-width: 720px;

    margin: 0 auto;

    color: ${MUTED};

    font-size: clamp(16px, 1.2vw, 19px);
    line-height: 1.6;
  }
`

/* =========================================================
   SEARCH
   ========================================================= */

const SearchArea = styled.div`
  width: min(760px, 100%);

  margin:
    0
    auto
    clamp(32px, 5vw, 55px);
`

const SearchLabel = styled.div`
  margin-bottom: 9px;

  font-size: 14px;
  font-weight: 600;

  color: #30363d;
`

/* =========================================================
   COHORT GRID
   ========================================================= */

const CohortGrid = styled.div`
  display: grid;

  grid-template-columns:
    repeat(
      auto-fit,
      minmax(320px, 700px)
    );

  justify-content: center;

  gap: clamp(20px, 2vw, 32px);

  width: 100%;
  margin: 0 auto;
`

const CohortCard = styled(Link)`
  position: relative;

  width: 100%;
  box-sizing: border-box;

  padding: 0;

  border: 1px solid ${BORDER};
  border-radius: 14px;

  overflow: hidden;

  background: #fff;

  color: ${TEXT};
  text-decoration: none;

  display: flex;
  flex-direction: column;

  transition:
    transform 180ms ease,
    box-shadow 180ms ease,
    border-color 180ms ease;

  &:hover {
    transform: translateY(-6px);

    border-color: #aebbb3;

    box-shadow:
      0 12px 34px
      rgba(0, 60, 32, 0.12);
  }

  &:hover .cohort-action {
    background: ${DARK_GREEN};
  }

  &:hover .cohort-arrow {
    transform: translateX(5px);
  }
`

/* =========================================================
   CARD HEADER
   ========================================================= */

const CardHeader = styled.div`
  min-height: clamp(120px, 12vw, 150px);

  padding:
    clamp(22px, 3vw, 30px);

  box-sizing: border-box;

  background: ${({ $featured }) =>
        $featured
            ? 'linear-gradient(145deg, #08703d, #004d2b)'
            : 'linear-gradient(145deg, #f5faf7, #edf5f0)'};

  color: ${({ $featured }) =>
        $featured ? '#fff' : GREEN};

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: clamp(16px, 3vw, 30px);

  @media (max-width: 520px) {
    align-items: flex-start;
  }
`

const CohortIdentity = styled.div`
  min-width: 0;

  display: flex;
  flex-direction: column;

  gap: 8px;

  strong {
    font-size: clamp(36px, 5vw, 52px);
    line-height: 1;
  }

  span {
    font-size: clamp(11px, 1.2vw, 14px);

    font-weight: 600;

    opacity: 0.85;

    text-transform: uppercase;

    letter-spacing: 0.08em;

    line-height: 1.4;
  }
`

const Graphic = styled.div`
  width: clamp(64px, 8vw, 82px);
  height: clamp(64px, 8vw, 82px);

  flex: 0 0 auto;

  border-radius: 50%;
  overflow: hidden;

  border: 2px solid
    ${({ $featured }) =>
        $featured
            ? 'rgba(255,255,255,.5)'
            : 'rgba(0,85,46,.25)'};

  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 88%;
    height: 88%;
    object-fit: contain;
    display: block;
  }
`

/* =========================================================
   CARD BODY
   ========================================================= */

const CardBody = styled.div`
  flex: 1;

  padding:
    clamp(22px, 3vw, 30px)
    clamp(20px, 3vw, 30px)
    clamp(20px, 3vw, 26px);

  box-sizing: border-box;

  display: flex;
  flex-direction: column;

  h2 {
    margin: 0 0 12px;

    color: ${GREEN};

    font-size: clamp(20px, 2vw, 24px);
  }

  p {
    margin: 0;

    color: ${MUTED};

    font-size: clamp(14px, 1.2vw, 16px);
    line-height: 1.6;
  }
`

/* =========================================================
   STATS
   ========================================================= */

const Stats = styled.div`
  display: grid;

  grid-template-columns:
    repeat(2, minmax(0, 1fr));

  margin-top: clamp(22px, 3vw, 28px);

  border-top: 1px solid #e4e8eb;
  border-bottom: 1px solid #e4e8eb;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`

const Stat = styled.div`
  padding: 16px 0;

  &:nth-child(odd) {
    border-right: 1px solid #e4e8eb;
  }

  &:nth-child(even) {
    padding-left: 20px;
  }

  strong {
    display: block;

    color: ${GREEN};

    font-size: clamp(16px, 1.5vw, 18px);
  }

  span {
    display: block;

    margin-top: 4px;

    color: ${MUTED};

    font-size: 12px;
  }

  @media (max-width: 480px) {
    padding: 12px 0;

    &:nth-child(odd) {
      border-right: 0;
    }

    &:nth-child(even) {
      padding-left: 0;
    }

    &:not(:last-child) {
      border-bottom: 1px solid #e4e8eb;
    }
  }
`

/* =========================================================
   TAGS
   ========================================================= */

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;

  gap: 8px;

  margin-top: 22px;
`

const Tag = styled.span`
  padding: 6px 10px;

  border-radius: 20px;

  background: #f2f6f3;

  color: #31533f;

  font-size: 12px;
  font-weight: 600;
`

/* =========================================================
   CARD ACTION
   ========================================================= */

const CardAction = styled.div`
  min-height: clamp(54px, 5vw, 62px);

  box-sizing: border-box;

  padding:
    0
    clamp(20px, 3vw, 30px);

  background: ${GREEN};

  color: #fff;

  display: flex;
  align-items: center;
  justify-content: space-between;

  font-size: clamp(14px, 1.2vw, 16px);
  font-weight: 700;

  transition: background 180ms ease;
`

const Arrow = styled.span`
  display: inline-block;

  font-size: 20px;

  transition: transform 180ms ease;
`

/* =========================================================
   BOTTOM INFO
   ========================================================= */

const FooterInfo = styled.section`
  width: 100%;
  box-sizing: border-box;

  margin-top: clamp(45px, 7vh, 80px);

  padding:
    clamp(24px, 3vw, 30px)
    clamp(22px, 3vw, 34px);

  border: 1px solid ${BORDER};
  border-radius: 12px;

  display: grid;

  grid-template-columns:
    minmax(0, 1fr)
    auto;

  align-items: center;

  gap: 30px;

  background: #f8fbf9;

  h3 {
    margin: 0 0 8px;

    font-size: clamp(18px, 2vw, 20px);
  }

  p {
    margin: 0;

    color: ${MUTED};

    font-size: clamp(14px, 1.2vw, 16px);
    line-height: 1.5;
  }

  @media (max-width: 750px) {
    grid-template-columns: 1fr;
  }
`

const SmallButton = styled(Link)`
  padding: 11px 18px;

  border: 1px solid ${GREEN};
  border-radius: 5px;

  color: ${GREEN};

  background: #fff;

  text-decoration: none;

  font-weight: 600;

  white-space: nowrap;

  transition:
    background 180ms ease,
    color 180ms ease;

  &:hover {
    background: ${GREEN};
    color: #fff;
  }
`

/* =========================================================
   EXPLORE BY COHORT PAGE
   ========================================================= */

const ExploreByCohort = () => {
    return (
        <Page>

            <DocumentTitle title="Explore by Cohort" />

            <Main>

                {/* =================================================
            BREADCRUMBS
            ================================================= */}

                <Breadcrumbs>

                    <Link
                        preserveSelectedDataset={false}
                        to="/"
                    >
                        Home
                    </Link>

                    <span>›</span>

                    <b>Explore by Cohort</b>

                </Breadcrumbs>

                {/* =================================================
            INTRO
            ================================================= */}

                <Intro>

                    <h1>
                        Explore by Cohort
                    </h1>

                    <p>
                        Explore genomic and clinical datasets across disease-specific
                        cohorts. Select a cohort below to browse variants, genomic
                        features, population data, and available resources.
                    </p>

                </Intro>

                {/* =================================================
            SEARCH
            ================================================= */}

                <SearchArea>

                    <SearchLabel>
                        Search across all cohorts
                    </SearchLabel>

                    <Searchbox
                        width="100%"
                    />

                </SearchArea>

                {/* =================================================
            COHORTS
            ================================================= */}

                <CohortGrid>

                    {/*
          =====================================================
          CONGENITAL HYDROCEPHALUS
          =====================================================

          <CohortCard
            preserveSelectedDataset={false}
            to="/cohort/CH"
          >

            <CardHeader>

              <CohortIdentity>
                <strong>CH</strong>
                <span>Congenital Hydrocephalus</span>
              </CohortIdentity>

              <Graphic>
                ◉
              </Graphic>

            </CardHeader>

            <CardBody>

              <h2>
                Congenital Hydrocephalus
              </h2>

              <p>
                Explore genomic variation and gene associations related
                to congenital hydrocephalus.
              </p>

              <Stats>

                <Stat>
                  <strong>WGS</strong>
                  <span>Genomic Data</span>
                </Stat>

                <Stat>
                  <strong>GRCh38</strong>
                  <span>Reference Build</span>
                </Stat>

              </Stats>

              <Tags>
                <Tag>Variants</Tag>
                <Tag>Genes</Tag>
                <Tag>Genomics</Tag>
              </Tags>

            </CardBody>

            <CardAction className="cohort-action">

              Explore CH

              <Arrow className="cohort-arrow">
                →
              </Arrow>

            </CardAction>

          </CohortCard>
          */}

                    {/*
          =====================================================
          CEREBRAL PALSY
          =====================================================

          <CohortCard
            preserveSelectedDataset={false}
            to="/cohort/CP"
          >

            <CardHeader>

              <CohortIdentity>
                <strong>CP</strong>
                <span>Cerebral Palsy</span>
              </CohortIdentity>

              <Graphic>
                ⌬
              </Graphic>

            </CardHeader>

            <CardBody>

              <h2>
                Cerebral Palsy
              </h2>

              <p>
                Discover variants and genomic findings across
                individuals within the cerebral palsy cohort.
              </p>

              <Stats>

                <Stat>
                  <strong>WGS</strong>
                  <span>Genomic Data</span>
                </Stat>

                <Stat>
                  <strong>GRCh38</strong>
                  <span>Reference Build</span>
                </Stat>

              </Stats>

              <Tags>
                <Tag>Variants</Tag>
                <Tag>Genes</Tag>
                <Tag>Rare Disease</Tag>
              </Tags>

            </CardBody>

            <CardAction className="cohort-action">

              Explore CP

              <Arrow className="cohort-arrow">
                →
              </Arrow>

            </CardAction>

          </CohortCard>
          */}

                    {/* =================================================
              IPN
              ================================================= */}

                    <CohortCard
                        preserveSelectedDataset={false}
                        to="/cohort/IPN"
                    >

                        <CardHeader $featured>

                            <CohortIdentity>

                                <strong>
                                    IPN
                                </strong>

                                <span>
                                    Idiopathic Peripheral Neuropathy
                                </span>

                            </CohortIdentity>

                            <Graphic $featured>
                                <img src={ipnLogo} alt="IPN logo" />
                            </Graphic>

                        </CardHeader>

                        <CardBody>

                            <h2>
                                Idiopathic Peripheral Neuropathy
                            </h2>

                            <p>
                                Investigate genomic and phenotype data from the IPN
                                cohort, including population structure, tandem repeats,
                                SNVs, small variants, and structural variation.
                            </p>

                            <Stats>

                                <Stat>
                                    <strong>788</strong>
                                    <span>Samples</span>
                                </Stat>

                                <Stat>
                                    <strong>~90x</strong>
                                    <span>Median Coverage</span>
                                </Stat>

                                <Stat>
                                    <strong>WGS</strong>
                                    <span>Genomic Data</span>
                                </Stat>

                                <Stat>
                                    <strong>GRCh38</strong>
                                    <span>Reference Build</span>
                                </Stat>

                            </Stats>

                            <Tags>

                                <Tag>
                                    Population
                                </Tag>

                                <Tag>
                                    Tandem Repeats
                                </Tag>

                                <Tag>
                                    SNVs
                                </Tag>

                                <Tag>
                                    Structural Variants
                                </Tag>

                            </Tags>

                        </CardBody>

                        <CardAction className="cohort-action">

                            Explore IPN

                            <Arrow className="cohort-arrow">
                                →
                            </Arrow>

                        </CardAction>

                    </CohortCard>

                </CohortGrid>

                {/* =================================================
            FOOTER INFO
            ================================================= */}

                <FooterInfo>

                    <div>

                        <h3>
                            Looking for a specific gene or variant?
                        </h3>

                        <p>
                            Use the search above to search across available cohorts,
                            or browse an individual cohort for cohort-specific
                            analyses and resources.
                        </p>

                    </div>

                    <SmallButton
                        preserveSelectedDataset={false}
                        to="/"
                    >
                        Return Home
                    </SmallButton>

                </FooterInfo>

            </Main>

        </Page>
    )
}

export default ExploreByCohort